import React, { createContext, useContext, useEffect, useCallback, useRef } from 'react';
import appBranding from '@happylife-a/branding';
import utils from '@happylife-a/utils';
import { useGetFavoritesIds, useToggleFavorite } from '../core/hooks/favoriteHooks';
import { useGetFavoritesV2Ids, useToggleFavoriteV2 } from '../core/hooks/v2/favoriteHooksV2';
import { useUser } from './UserContext';

export const FavoriteContext = createContext({
  favorites: [],
  toggleFavorite: () => {},
  isFavorite: () => {},
});

export function useFavorites() {
  const context = useContext(FavoriteContext);
  if (!context) {
    throw new Error('Please use hook inside of FavoriteProvider.');
  }

  return context;
}

export function FavoriteProvider({ children }) {
  const isV2ApiEnabled = appBranding.tools.features.isEnabled('UsesV2ApiFlow');
  const { user, isUnknownUser, isLoading: isLoadingUser } = useUser();

  // V1 favorites
  const { data: v1Favorites, refetch: loadV1Favorites } = useGetFavoritesIds({
    enabled: false,
  });

  // V2 favorites
  const { data: v2Favorites, refetch: loadV2Favorites } = useGetFavoritesV2Ids({
    enabled: false,
  });

  const favorites = isV2ApiEnabled ? v2Favorites : v1Favorites;

  useEffect(() => {
    if (isUnknownUser || isLoadingUser || !user?.id) {
      return;
    }

    if (isV2ApiEnabled) {
      loadV2Favorites();
    } else {
      loadV1Favorites();
    }
  }, [isUnknownUser, isLoadingUser, user?.id, isV2ApiEnabled]);

  const refFavorites = useRef();
  refFavorites.current = favorites;

  const isFavoriteV1 = useCallback((productDetailId) => {
    if (!refFavorites.current || !Array.isArray(refFavorites.current.ids)) {
      return false;
    }

    const index = refFavorites.current.ids.findIndex((id) =>
      utils.helpers.id.same(id.productDetailId, productDetailId)
    );

    return index > -1;
  }, []);

  const isFavoriteV2 = useCallback((productVariantId) => {
    if (!refFavorites.current || !Array.isArray(refFavorites.current.ids)) {
      return false;
    }

    const index = refFavorites.current.ids.findIndex((id) =>
      utils.helpers.id.same(id.productVariantId, productVariantId)
    );

    return index > -1;
  }, []);

  const isFavorite = isV2ApiEnabled ? isFavoriteV2 : isFavoriteV1;

  const toggleV1 = useToggleFavorite(isFavoriteV1);
  const toggleV2 = useToggleFavoriteV2(isFavoriteV2);
  const toggleFavorite = isV2ApiEnabled ? toggleV2 : toggleV1;

  return (
    <FavoriteContext.Provider
      value={{
        favorites: favorites,
        isFavorite: isFavorite,
        toggleFavorite: toggleFavorite,
      }}
    >
      {children}
    </FavoriteContext.Provider>
  );
}
